// Gate 5.2 · Publish Queue
// Operational source of truth for every scheduled publish action.
// Chronological list with filters, per-row actions, and a compact
// scheduler heartbeat. The Calendar view is a projection over the same
// data model; changes made here reflect there and vice versa.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";
import {
  channelDisplay,
  fmtDateTimeTz,
  parseDatetimeLocalInTz,
  toDatetimeLocalInTz,
  useOrgTimezone,
} from "../lib/tz";

const STATE_TONE = {
  scheduled: "muted",
  executing: "warn",
  executed: "success",
  failed: "danger",
  canceled: "muted",
  paused_by_operator: "warn",
  paused_unhealthy_connection: "danger",
  missed: "danger",
};

const STATE_LABEL = {
  scheduled: "Scheduled",
  executing: "Sending",
  executed: "Sent",
  failed: "Failed",
  canceled: "Canceled",
  paused_by_operator: "Paused (operator)",
  paused_unhealthy_connection: "Paused (unhealthy connection)",
  missed: "Missed",
};

// Plain-English explanation of every scheduler state, rendered inline
// under the pill so operators never have to guess what a label means.
const STATE_EXPLAINER = {
  scheduled: "Waiting for its scheduled_for time. Scheduler will pick it up on the next tick.",
  executing: "The scheduler is actively hitting the provider right now.",
  executed: "Successfully posted. See the linked receipt for the external URL and provider ID.",
  failed: "The provider rejected the publish attempt. See the last_error and remediation below.",
  canceled: "An operator canceled this row. It will not be retried.",
  paused_by_operator: "An operator paused this row. Resume it or reschedule when you're ready.",
  paused_unhealthy_connection: "The bound channel connection is unhealthy (token rejected, expired, or provider outage). Fix the connection in Channel Connections; this row auto-resumes when it goes HEALTHY.",
  missed: "The scheduled time passed before we could pick it up (usually because the scheduler was down). Reschedule if you still want to publish.",
};

const CHANNEL_LABEL = {
  facebook:      "Facebook",
  instagram:     "Instagram",
  youtube:       "YouTube",
  tiktok:        "TikTok",
  pinterest:     "Pinterest",
  email:         "Email",
  manual_export: "Manual Export",
};

const FILTER_OPTIONS = [
  { value: "", label: "All states" },
  { value: "scheduled", label: "Scheduled" },
  { value: "executing", label: "Executing" },
  { value: "paused_by_operator", label: "Paused (operator)" },
  { value: "paused_unhealthy_connection", label: "Paused (unhealthy)" },
  { value: "executed", label: "Executed" },
  { value: "failed", label: "Failed" },
  { value: "canceled", label: "Canceled" },
  { value: "missed", label: "Missed" },
];

function fmtDateTime(iso) {
  if (!iso) return "—";
  return fmtDateTimeTz(iso);
}

function fmtRelativeFromNow(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const delta = Math.round((then - now) / 1000);
  const abs = Math.abs(delta);
  if (abs < 60) return delta >= 0 ? `in ${abs}s` : `${abs}s ago`;
  if (abs < 3600) return delta >= 0 ? `in ${Math.round(abs / 60)}m` : `${Math.round(abs / 60)}m ago`;
  if (abs < 86400) return delta >= 0 ? `in ${Math.round(abs / 3600)}h` : `${Math.round(abs / 3600)}h ago`;
  return delta >= 0 ? `in ${Math.round(abs / 86400)}d` : `${Math.round(abs / 86400)}d ago`;
}

/* -------------------------------------------------------------------------- */
/*  Reschedule modal                                                          */
/* -------------------------------------------------------------------------- */

function RescheduleForm({ schedule, onCancel, onRescheduled }) {
  const tz = useOrgTimezone();
  const [when, setWhen] = useState(() =>
    toDatetimeLocalInTz(schedule.scheduled_for, tz) || "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const parsed = parseDatetimeLocalInTz(when, tz);
      if (!parsed) throw new Error("Invalid date/time");
      const iso = parsed.toISOString();
      const r = await api.patch(`/publish-schedules/${schedule.id}`, {
        scheduled_for: iso,
      });
      onRescheduled(r.data);
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }} data-testid="reschedule-form">
      <ErrorBanner error={error} testId="reschedule-error" />
      <div className="bo-hint" data-testid="reschedule-context">
        Rescheduling <strong>{schedule.publish_plan_id.slice(0, 8)}…</strong> on
        {" "}{channelDisplay(schedule.channel)}.
        {["paused_by_operator", "paused_unhealthy_connection"].includes(schedule.state) && (
          <span> Rescheduling a paused row implicitly resumes it.</span>
        )}
      </div>
      <div className="bo-field">
        <label className="bo-label">
          New scheduled time ({tz.timezone}{tz.abbr ? ` · ${tz.abbr}` : ""})
        </label>
        <input
          className="bo-input"
          type="datetime-local"
          value={when}
          data-testid="reschedule-input"
          onChange={(e) => setWhen(e.target.value)}
          required
        />
        <div className="bo-hint">
          Interpreted in your org timezone and converted to UTC on submit.
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="bo-btn bo-btn-ghost" data-testid="reschedule-cancel" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="bo-btn bo-btn-primary" data-testid="reschedule-submit" disabled={busy || !when}>
          {busy ? "Saving…" : "Reschedule"}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Row                                                                       */
/* -------------------------------------------------------------------------- */

function ScheduleRow({ schedule, onAction, onReschedule }) {
  const terminal = ["executed", "failed", "canceled", "missed"].includes(schedule.state);
  const paused = ["paused_by_operator", "paused_unhealthy_connection"].includes(schedule.state);
  return (
    <div
      className="bo-card"
      data-testid={`schedule-row-${schedule.id}`}
      style={terminal ? { opacity: 0.72 } : undefined}
    >
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 320px", minWidth: 240 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Pill tone={STATE_TONE[schedule.state] || "muted"} testId={`schedule-state-${schedule.id}`}>
              {STATE_LABEL[schedule.state] || schedule.state}
            </Pill>
            <span style={{ fontSize: 13, color: "var(--bo-muted)" }} data-testid={`schedule-channel-${schedule.id}`}>
              {channelDisplay(schedule.channel)}
            </span>
          </div>
          {STATE_EXPLAINER[schedule.state] && (
            <div
              data-testid={`schedule-state-explainer-${schedule.id}`}
              style={{ marginTop: 6, fontSize: 12, color: "var(--bo-muted)", lineHeight: 1.45, maxWidth: 640 }}
            >
              {STATE_EXPLAINER[schedule.state]}
            </div>
          )}
          <div style={{ marginTop: 8, fontFamily: "var(--bo-serif)", fontSize: 17, color: "var(--bo-ink)" }} data-testid={`schedule-time-${schedule.id}`}>
            {fmtDateTime(schedule.scheduled_for)}
          </div>
          <div style={{ marginTop: 2, fontSize: 12, color: "var(--bo-muted)" }}>
            {fmtRelativeFromNow(schedule.scheduled_for)}
            {schedule.attempts > 0 && (
              <>
                <span style={{ margin: "0 8px" }}>·</span>
                <span data-testid={`schedule-attempts-${schedule.id}`}>{schedule.attempts} attempt{schedule.attempts === 1 ? "" : "s"}</span>
              </>
            )}
          </div>
          <div className="bo-mono" style={{ marginTop: 6, fontSize: 12, color: "var(--bo-muted)" }}>
            plan {schedule.publish_plan_id?.slice(0, 8) || "—"} · sched {schedule.id.slice(0, 8)}
          </div>
          {schedule.note && (
            <div style={{ marginTop: 6, fontSize: 13, color: "var(--bo-ink-soft)", fontStyle: "italic" }} data-testid={`schedule-note-${schedule.id}`}>
              “{schedule.note}”
            </div>
          )}
          {schedule.last_error && (
            <div
              style={{ marginTop: 6, fontSize: 12, color: "var(--bo-danger)", lineHeight: 1.4, maxWidth: 640 }}
              data-testid={`schedule-error-${schedule.id}`}
              title={schedule.last_error}
            >
              {schedule.last_error.length > 240 ? schedule.last_error.slice(0, 240) + "…" : schedule.last_error}
            </div>
          )}
          {schedule.execution_receipt_id && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              <Link
                to={`/brand-os/publish/receipts/${schedule.execution_receipt_id}`}
                data-testid={`schedule-receipt-link-${schedule.id}`}
              >
                View receipt →
              </Link>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
          {schedule.state === "scheduled" && (
            <button className="bo-btn bo-btn-ghost" data-testid={`schedule-pause-${schedule.id}`} onClick={() => onAction("pause", schedule)}>Pause</button>
          )}
          {paused && (
            <button className="bo-btn bo-btn-ghost" data-testid={`schedule-resume-${schedule.id}`} onClick={() => onAction("resume", schedule)}>Resume</button>
          )}
          {!terminal && (
            <>
              <button className="bo-btn bo-btn-ghost" data-testid={`schedule-reschedule-${schedule.id}`} onClick={() => onReschedule(schedule)}>Reschedule</button>
              <button className="bo-btn bo-btn-ghost" data-testid={`schedule-cancel-${schedule.id}`} onClick={() => onAction("cancel", schedule)} style={{ color: "var(--bo-danger)" }}>Cancel</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main page                                                                 */
/* -------------------------------------------------------------------------- */

export default function PublishQueue() {
  const tz = useOrgTimezone();
  const [state, setState] = useState({ loading: true, error: null });
  const [schedules, setSchedules] = useState([]);
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [status, setStatus] = useState({ running: null });
  const [filterState, setFilterState] = useState("");
  const [acceptanceOnly, setAcceptanceOnly] = useState(false);
  const [flash, setFlash] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  // P5 (2026-11-02) — Global posting hold banner + owner toggle.
  const [orgSettings, setOrgSettings] = useState(null);
  const [holdBusy, setHoldBusy] = useState(false);
  // P6 (2026-11-02) — Publish health badge + open incident count.
  const [health, setHealth] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const r = await api.get("/publish/health");
      setHealth(r.data || null);
    } catch {
      setHealth(null);
    }
  }, []);

  const loadOrgSettings = useCallback(async () => {
    try {
      const r = await api.get("/organizations/current/settings");
      setOrgSettings(r.data || {});
    } catch (e) {
      // Non-fatal — queue is still usable without the banner.
      setOrgSettings({ posting_hold: false, role: null });
    }
  }, []);

  async function handleTogglePostingHold() {
    if (!orgSettings) return;
    const next = !orgSettings.posting_hold;
    let reason = null;
    if (next) {
      reason = window.prompt(
        "Turn posting hold ON? Scheduler and manual publish will refuse everything except acceptance tests until you turn it off. Reason (optional):",
        "",
      );
      if (reason === null) return; // user cancelled
    } else {
      if (!window.confirm(
        "Turn posting hold OFF? Scheduled posts will resume on the next tick.",
      )) return;
    }
    setHoldBusy(true);
    try {
      await api.put(
        "/organizations/current/settings/posting-hold",
        { enabled: next, reason: reason || null },
      );
      setFlash(next ? "Posting hold ON. Scheduler will skip non-acceptance rows."
                     : "Posting hold OFF. Scheduler resumed.");
      await loadOrgSettings();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally {
      setHoldBusy(false);
    }
  }

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const q = new URLSearchParams();
      if (filterState) q.set("state", filterState);
      const [r_schedules, r_status, r_receipts] = await Promise.all([
        api.get(`/publish-schedules?${q.toString()}`),
        api.get("/publish-scheduler/status"),
        api.get(
          "/publish-receipts?limit=10"
          + (acceptanceOnly ? "&acceptance_only=true" : "")
        ).catch(() => ({ data: { receipts: [] } })),
      ]);
      setSchedules(r_schedules.data.schedules || []);
      setStatus(r_status.data || {});
      setRecentReceipts(r_receipts.data?.receipts || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [filterState, acceptanceOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOrgSettings(); }, [loadOrgSettings]);
  useEffect(() => { loadHealth(); }, [loadHealth]);

  const groups = useMemo(() => {
    // Partition into pending / terminal so operators see actionable rows first.
    const pending = [];
    const terminal = [];
    for (const s of schedules) {
      if (["executed", "failed", "canceled", "missed"].includes(s.state)) terminal.push(s);
      else pending.push(s);
    }
    return { pending, terminal };
  }, [schedules]);

  async function handleAction(action, schedule) {
    const path = {
      pause: `/publish-schedules/${schedule.id}/pause`,
      resume: `/publish-schedules/${schedule.id}/resume`,
      cancel: `/publish-schedules/${schedule.id}/cancel`,
    }[action];
    if (!path) return;
    if (action === "cancel") {
      if (!window.confirm(`Cancel this scheduled ${schedule.channel} publish? This cannot be undone; you can create a new schedule any time.`)) return;
    }
    try {
      await api.post(path);
      setFlash(`${action[0].toUpperCase() + action.slice(1)}d schedule ${schedule.id.slice(0, 8)}.`);
      load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  async function handleTickNow() {
    try {
      const r = await api.post("/publish-scheduler/tick-now");
      const { processed, missed } = r.data || {};
      setFlash(`Tick fired · processed ${processed ?? 0} · marked ${missed ?? 0} missed.`);
      load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  return (
    <Shell title="Publish Queue">
      <div className="bo-page" data-testid="publish-queue-page">
        {/* P5 (2026-11-02) — Global posting hold banner. Visible to
            everyone; only owners see the toggle button. */}
        {orgSettings?.posting_hold && (
          <div
            data-testid="posting-hold-banner"
            style={{
              background: "#5c0f0f",
              color: "#fff",
              padding: "12px 16px",
              borderRadius: 10,
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 14,
              flexWrap: "wrap",
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            }}
          >
            <strong style={{ fontSize: 14 }}>
              Posting hold is ON — scheduler is paused.
            </strong>
            <span style={{ fontSize: 13, opacity: 0.95 }}>
              Only acceptance-test plans publish while this is on.
              {orgSettings.posting_hold_reason
                ? ` Reason: ${orgSettings.posting_hold_reason}`
                : ""}
            </span>
            {orgSettings.role === "owner" && (
              <button
                className="bo-btn"
                data-testid="posting-hold-turn-off-btn"
                disabled={holdBusy}
                onClick={handleTogglePostingHold}
                style={{
                  marginLeft: "auto",
                  background: "#fff",
                  color: "#5c0f0f",
                  fontWeight: 600,
                  padding: "6px 12px",
                  fontSize: 13,
                  border: 0,
                }}
              >
                {holdBusy ? "Working…" : "Turn OFF posting hold"}
              </button>
            )}
          </div>
        )}
        {orgSettings && !orgSettings.posting_hold
          && orgSettings.role === "owner" && (
          <div
            data-testid="posting-hold-off-strip"
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "6px 10px", marginBottom: 10,
              fontSize: 12, color: "var(--bo-muted)",
            }}
          >
            <span>Posting hold is OFF.</span>
            <button
              className="bo-btn bo-btn-ghost"
              data-testid="posting-hold-turn-on-btn"
              disabled={holdBusy}
              onClick={handleTogglePostingHold}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              {holdBusy ? "Working…" : "Turn ON posting hold"}
            </button>
          </div>
        )}
        {/* -------- Header + scheduler heartbeat -------- */}
        <section className="bo-card" data-testid="publish-queue-header">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 400px", minWidth: 260 }}>
              <div className="bo-eyebrow">Publish Infrastructure</div>
              <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 4px" }}>Queue</h1>
              <p style={{ color: "var(--bo-muted)", maxWidth: 720, lineHeight: 1.55, marginTop: 4 }}>
                Every publish action Brand OS is holding — future, in-flight, and archival —
                in chronological order. The scheduler ticks every {status.tick_interval_seconds || "…"} seconds; a
                paused or unhealthy connection halts affected rows automatically so we never
                hammer the external platform. All times shown in <strong data-testid="queue-tz-label">{tz.timezone}{tz.abbr ? ` (${tz.abbr})` : ""}</strong>.
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 220, alignItems: "flex-end" }}>
              <Pill
                tone={status.running ? "success" : "danger"}
                testId="scheduler-heartbeat"
              >
                {status.running ? `Scheduler running (${status.worker_id?.slice(0, 16) || "…"})` : "Scheduler stopped"}
              </Pill>
              <Link
                to="/brand-os/publish/calendar"
                className="bo-btn bo-btn-ghost"
                data-testid="queue-open-calendar"
                style={{ textDecoration: "none" }}
              >
                Open calendar →
              </Link>
              <Link
                to="/brand-os/publish/fan-out"
                className="bo-btn bo-btn-ghost"
                data-testid="queue-open-fan-out"
                style={{ textDecoration: "none" }}
              >
                New fan-out →
              </Link>
              <Link
                to="/brand-os/publish/schedule-week"
                className="bo-btn bo-btn-ghost"
                data-testid="queue-open-week-scheduler"
                style={{ textDecoration: "none" }}
              >
                Week scheduler →
              </Link>
              {/* P6 — Adapter Call Explorer (owner-only; backend
                  enforces role, link is visible to all for discovery). */}
              <Link
                to="/brand-os/publish/adapter-calls"
                className="bo-btn bo-btn-ghost"
                data-testid="queue-open-adapter-calls"
                style={{ textDecoration: "none" }}
              >
                Adapter calls →
              </Link>
              {/* publish-87 round-11 — TikTok Direct Post UX + results */}
              <Link
                to="/brand-os/publish/tiktok"
                className="bo-btn bo-btn-ghost"
                data-testid="queue-open-tiktok-post"
                style={{ textDecoration: "none" }}
              >
                Post to TikTok →
              </Link>
              <Link
                to="/brand-os/publish/tiktok/results"
                className="bo-btn bo-btn-ghost"
                data-testid="queue-open-tiktok-results"
                style={{ textDecoration: "none" }}
              >
                TikTok results →
              </Link>
              {health && (
                <Link
                  to="/brand-os/publish/health"
                  data-testid="publish-health-badge"
                  title={
                    `heartbeat: ${health.scheduler_heartbeat_age_s ?? 'n/a'}s`
                    + ` · hold: ${health.posting_hold ? 'ON' : 'OFF'}`
                    + ` · open Health page`
                  }
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "6px 10px", borderRadius: 999, fontSize: 12,
                    background: health.scheduler_stalled
                      ? "#5c0f0f" : "#1f6c3f",
                    color: "#fff", textDecoration: "none",
                  }}
                >
                  <span>{health.scheduler_stalled ? "STALLED" : "HEALTHY"}</span>
                  {health.open_incident_count > 0 && (
                    <span data-testid="publish-health-incidents"
                          style={{ background: "#fff", color: "#5c0f0f",
                                   borderRadius: 999, padding: "0 6px",
                                   fontSize: 11, fontWeight: 700 }}>
                      {health.open_incident_count} incident{health.open_incident_count === 1 ? "" : "s"}
                    </span>
                  )}
                </Link>
              )}
              <button
                className="bo-btn bo-btn-ghost"
                data-testid="queue-tick-now"
                onClick={handleTickNow}
              >
                Run tick now
              </button>
            </div>
          </div>
          <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label className="bo-label" style={{ margin: 0 }}>Filter:</label>
            <select
              className="bo-input"
              data-testid="queue-filter-state"
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              style={{ width: 220 }}
            >
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--bo-muted)" }} data-testid="queue-count">
              {schedules.length} schedule{schedules.length === 1 ? "" : "s"} loaded
            </span>
          </div>
          {flash && (
            <div className="bo-flash" data-testid="publish-queue-flash" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)", color: "var(--bo-ink)", fontSize: 13 }}>
              {flash}
            </div>
          )}
        </section>

        {/* -------- Recent Activity (top of page — the actual audit trail) -------- */}
        <RecentActivityPanel
          receipts={recentReceipts}
          acceptanceOnly={acceptanceOnly}
          onToggleAcceptance={setAcceptanceOnly}
        />

        {/* -------- Lists -------- */}
        <section style={{ marginTop: 20 }}>
          <ErrorBanner error={state.error} testId="publish-queue-error" />
          {state.loading ? (
            <LoadingCard label="Loading schedules…" />
          ) : schedules.length === 0 ? (
            <EmptyState
              testId="publish-queue-empty"
              title="Queue is empty"
              description="No publish actions in this filter yet. Schedule your next post from an approved asset set."
            />
          ) : (
            <>
              {groups.pending.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div className="bo-eyebrow" style={{ margin: "0 0 8px 0" }}>Pending · {groups.pending.length}</div>
                  <div style={{ display: "grid", gap: 12 }} data-testid="publish-queue-pending">
                    {groups.pending.map((s) => (
                      <ScheduleRow key={s.id} schedule={s} onAction={handleAction} onReschedule={setRescheduleTarget} />
                    ))}
                  </div>
                </div>
              )}
              {groups.terminal.length > 0 && (
                <div>
                  <div className="bo-eyebrow" style={{ margin: "0 0 8px 0" }}>Archive · {groups.terminal.length}</div>
                  <div style={{ display: "grid", gap: 12 }} data-testid="publish-queue-terminal">
                    {groups.terminal.map((s) => (
                      <ScheduleRow key={s.id} schedule={s} onAction={handleAction} onReschedule={setRescheduleTarget} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        {/* -------- Reschedule modal -------- */}
        {rescheduleTarget && (
          <div
            className="bo-modal-backdrop"
            data-testid="reschedule-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.currentTarget === e.target) setRescheduleTarget(null); }}
            style={{ position: "fixed", inset: 0, background: "rgba(26,26,46,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}
          >
            <div className="bo-card" style={{ width: "min(540px, 92vw)", background: "var(--bo-panel)", boxShadow: "var(--bo-shadow-lg)" }}>
              <div className="bo-eyebrow">Reschedule</div>
              <h2 className="bo-serif" style={{ fontSize: 20, margin: "6px 0 14px" }}>Move scheduled publish</h2>
              <RescheduleForm
                schedule={rescheduleTarget}
                onCancel={() => setRescheduleTarget(null)}
                onRescheduled={(row) => {
                  setRescheduleTarget(null);
                  setFlash(`Rescheduled to ${fmtDateTime(row.scheduled_for)}.`);
                  load();
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// RecentActivityPanel — the actual audit trail
//
// Roger 2026-08-18: the queue-of-schedules was making it impossible to
// find "what did the system actually do in the last hour?" Every publish
// (Publish Now OR scheduler-driven) writes a Publish Receipt; this panel
// lists the last 10 with the same clarity rules as everything else:
//   * status pill (success / failed / partial)
//   * plain-English explainer per state
//   * external URL on success (one click to see the FB post)
//   * error + remediation on failure
//   * connection name + timestamp so operators always know what happened
// ---------------------------------------------------------------------------
function RecentActivityPanel({ receipts, acceptanceOnly, onToggleAcceptance }) {
  if (!receipts || receipts.length === 0) {
    return (
      <section
        className="bo-card"
        data-testid="publish-queue-recent-activity-empty"
        style={{ marginTop: 20 }}
      >
        <div className="bo-eyebrow">Recent Activity</div>
        <h2 className="bo-serif" style={{ fontSize: 18, margin: "6px 0 4px" }}>
          No publishes yet
        </h2>
        <p style={{ color: "var(--bo-muted)", fontSize: 13, lineHeight: 1.5 }}>
          Every time Brand OS publishes to a channel (via Publish Now or the
          scheduler) it writes an immutable Publish Receipt. Those receipts
          appear here in reverse-chronological order and are your audit trail.
          When you run your first publish, it lands here in seconds.
        </p>
      </section>
    );
  }
  return (
    <section
      className="bo-card"
      data-testid="publish-queue-recent-activity"
      style={{ marginTop: 20 }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div className="bo-eyebrow">Recent Activity</div>
          <h2 className="bo-serif" style={{ fontSize: 20, margin: "6px 0 4px" }}>
            Last {receipts.length} publish{receipts.length === 1 ? "" : "es"}
          </h2>
        </div>
        {typeof onToggleAcceptance === "function" && (
          <label
            data-testid="queue-acceptance-filter-label"
            style={{ fontSize: 12, display: "flex", gap: 6,
                     alignItems: "center", cursor: "pointer",
                     padding: "4px 10px", borderRadius: 6,
                     border: acceptanceOnly
                       ? "1px solid var(--bo-primary)"
                       : "1px solid var(--bo-line)",
                     background: acceptanceOnly
                       ? "var(--bo-primary-soft, #f1ece0)"
                       : "transparent" }}
          >
            <input
              type="checkbox"
              checked={!!acceptanceOnly}
              onChange={(e) => onToggleAcceptance(e.target.checked)}
              data-testid="queue-acceptance-filter-checkbox"
            />
            Acceptance tests only
          </label>
        )}
      </div>
      <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
        {receipts.map((r) => (
          <RecentReceiptRow key={r.id} receipt={r} />
        ))}
      </div>
    </section>
  );
}

function receiptDisplayStatus(receipt) {
  // Round 22 — receipts persist execution_status; the server also sends a
  // normalized `status`. Never infer "failed" from a missing legacy field.
  const exec = (receipt.execution_status || "").toLowerCase();
  if (exec === "published" || exec === "ok") return "success";
  if (exec === "failed") return "failed";
  if (exec === "pending_platform" || exec === "publishing" || exec === "live_run_pending") return "pending";
  if (exec === "live_run_cancelled") return "cancelled";
  const s = (receipt.status || "").toLowerCase();
  if (["success", "failed", "partial", "pending", "cancelled"].includes(s)) return s;
  if (receipt.external_artifact_id || receipt.external_id) return "success";
  if (receipt.failure_envelope) return "failed";
  return "pending";
}

function RecentReceiptRow({ receipt }) {
  const status = receiptDisplayStatus(receipt);
  const externalUrl = receipt.external_url || receipt.external_artifact_url;
  const planId = receipt.plan_id || receipt.publish_plan_id;
  const errorText = receipt.error
    || (status === "failed" && receipt.failure_envelope
        ? [receipt.failure_envelope.message, receipt.failure_envelope.provider_error].filter(Boolean).join(" — ")
        : "");
  const tone = status === "success" ? "success"
    : status === "partial" ? "warn"
    : status === "failed" ? "danger"
    : status === "pending" ? "warn"
    : "muted";
  const label = status === "success" ? "Published"
    : status === "partial" ? "Partial"
    : status === "failed" ? "Failed"
    : status === "pending" ? "Processing on platform"
    : status === "cancelled" ? "Cancelled"
    : status;
  const when = receipt.published_at || receipt.created_at;
  const conn = receipt.connection || {};
  const channelLbl = channelDisplay(conn.channel || receipt.channel);

  return (
    <div
      data-testid={`recent-receipt-${receipt.id}`}
      className="bo-card"
      style={{
        padding: 12,
        borderLeft: `3px solid ${
          status === "success" ? "var(--bo-success)"
            : status === "partial" ? "var(--bo-warn)"
              : status === "failed" ? "var(--bo-danger)"
                : "var(--bo-line)"
        }`,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 300px", minWidth: 240 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Pill tone={tone} testId={`recent-receipt-status-${receipt.id}`}>{label}</Pill>
            <span style={{ fontSize: 13, color: "var(--bo-muted)" }}>{channelLbl}</span>
            {conn.display_name && (
              <span
                style={{ fontSize: 13, color: "var(--bo-ink-soft)" }}
                data-testid={`recent-receipt-connection-${receipt.id}`}
              >
                via {conn.display_name}
              </span>
            )}
          </div>
          <div style={{ marginTop: 6, fontFamily: "var(--bo-serif)", fontSize: 15 }}>
            {fmtDateTime(when)}
            <span style={{ marginLeft: 8, fontSize: 12, color: "var(--bo-muted)", fontFamily: "inherit" }}>
              ({fmtRelativeFromNow(when)})
            </span>
          </div>
          <div className="bo-mono" style={{ marginTop: 4, fontSize: 12, color: "var(--bo-muted)" }}>
            receipt {receipt.id.slice(0, 8)}
            {planId && <> · plan {planId.slice(0, 8)}</>}
          </div>
          {receipt.reel_cut_shipped && receipt.reel_cut_shipped.cut_key && (
            <div
              data-testid={`recent-receipt-cut-shipped-${receipt.id}`}
              style={{
                marginTop: 6, padding: "6px 8px", borderRadius: 6,
                background: "var(--bo-panel-alt, #faf8f2)",
                border: "1px solid var(--bo-line)",
                fontSize: 12, lineHeight: 1.4,
                color: "var(--bo-ink-soft)",
              }}
            >
              <strong>Cut shipped:</strong>{" "}
              <span data-testid={`recent-receipt-cut-key-${receipt.id}`}>
                {receipt.reel_cut_shipped.cut_key}
              </span>
              {" · "}
              <em data-testid={`recent-receipt-cut-source-${receipt.id}`}>
                {receipt.reel_cut_shipped.source || "auto"}
              </em>
              {receipt.reel_cut_shipped.reason && (
                <> · <span style={{ color: "var(--bo-muted)" }}>
                  {receipt.reel_cut_shipped.reason}
                </span></>
              )}
            </div>
          )}
          {errorText && status === "failed" && (
            <div
              data-testid={`recent-receipt-error-${receipt.id}`}
              style={{
                marginTop: 8, padding: "8px 10px", borderRadius: 6,
                background: "var(--bo-danger-soft)", color: "var(--bo-danger)",
                fontSize: 12, lineHeight: 1.45,
              }}
            >
              <strong>Provider error:</strong>{" "}
              {errorText.length > 300 ? errorText.slice(0, 300) + "…" : errorText}
            </div>
          )}
          {receipt.remediation && status === "failed" && (
            <div
              data-testid={`recent-receipt-remediation-${receipt.id}`}
              style={{
                marginTop: 6, padding: "8px 10px", borderRadius: 6,
                background: "var(--bo-panel-sunken)", fontSize: 13, lineHeight: 1.5,
              }}
            >
              <strong style={{ marginRight: 4 }}>What to do:</strong>
              <span>{receipt.remediation}</span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          {externalUrl && (
            <a
              href={externalUrl}
              target="_blank"
              rel="noreferrer"
              className="bo-btn bo-btn-primary"
              data-testid={`recent-receipt-external-${receipt.id}`}
              style={{ textDecoration: "none" }}
            >
              View on {channelLbl} ↗
            </a>
          )}
          <Link
            to={`/brand-os/publish/receipts/${receipt.id}`}
            className="bo-btn bo-btn-ghost"
            data-testid={`recent-receipt-detail-${receipt.id}`}
            style={{ textDecoration: "none" }}
          >
            View receipt →
          </Link>
        </div>
      </div>
    </div>
  );
}

